import { useEffect, useState } from 'react';
import { GAME_MODES } from '../game/modes';
import type { GameModeId, GameState } from '../game/types';
import { ModePicker } from './ModePicker';

interface GameOverlayProps {
  game: GameState;
  highScore: number;
  onStart: (mode?: GameModeId) => void;
  onResume: () => void;
}

export function GameOverlay({ game, highScore, onStart, onResume }: GameOverlayProps) {
  const [selectedMode, setSelectedMode] = useState<GameModeId>(game.mode);

  useEffect(() => {
    setSelectedMode(game.mode);
  }, [game.mode, game.status]);

  if (game.status === 'playing') return null;

  const selectedDefinition = GAME_MODES.find((mode) => mode.id === selectedMode) ?? GAME_MODES[0];
  const modePicker = (
    <ModePicker
      legend={game.status === 'gameOver' ? 'Next mode' : 'Choose a mode'}
      selectedMode={selectedMode}
      onSelect={setSelectedMode}
    />
  );

  if (game.status === 'idle') {
    return (
      <div className="game-overlay game-overlay--start">
        <span className="overlay-kicker">Codytris</span>
        <h2>Choose your game.</h2>
        {modePicker}
        <button type="button" className="primary-button" onClick={() => onStart(selectedMode)}>Start {selectedDefinition.name}</button>
      </div>
    );
  }

  if (game.status === 'paused') {
    return (
      <div className="game-overlay">
        <span className="overlay-kicker">Paused</span>
        <h2>Game paused.</h2>
        <p>{game.pauseReason === 'focus' ? 'The game paused automatically when the window lost focus.' : 'Resume when you are ready.'}</p>
        <button type="button" className="primary-button" onClick={onResume}>Resume</button>
      </div>
    );
  }

  return (
    <div className="game-overlay game-overlay--over">
      <span className="overlay-kicker">Final score</span>
      <h2>Game over.</h2>
      <div className="overlay-score"><span>Score</span><strong>{game.score.toLocaleString()}</strong></div>
      {game.score > 0 && game.score >= highScore && <p className="new-record">New personal best.</p>}
      {modePicker}
      <button type="button" className="primary-button" onClick={() => onStart(selectedMode)}>Play {selectedDefinition.name}</button>
    </div>
  );
}
